import { ARRIVAL_SOURCE } from '../core/events';
import { dedupeExpected, type ExpectedStandard } from '../core/expected-standards';
import { pickContext, type StandardsContext } from '../core/standards-context';
import { ROUTE_FROM_PLANNER } from './routes';

/**
 * The Planner's "Check my lesson" hand-off, from the fragment it arrived in to the banner and
 * the Standards check that read it (KTD4, KTD5).
 *
 * ## The link
 *
 * The Planner opens `https://collab.labs.240.org/from-planner#<fragment>` in a new tab. The
 * fragment is `&`-separated `key=value` pairs; every value is `encodeURIComponent`-encoded,
 * and in the two list values each *element* is encoded and the elements are joined with a
 * literal `,` (so a comma inside a code arrives as `%2C` and never splits it):
 *
 * | key       | required | value                                                            |
 * |-----------|----------|------------------------------------------------------------------|
 * | `v`       | yes      | `1`. Any other version is ignored whole.                         |
 * | `from`    | yes      | `planner`.                                                       |
 * | `ids`     | yes      | 1–20 Learning Commons `caseIdentifierUUID`s, comma-joined.       |
 * | `codes`   | yes      | Their `statementCode`s, same count and order, each 1–64 chars.   |
 * | `state`   | no       | Objectives' `JURISDICTIONS` label, e.g. `Texas`.                 |
 * | `subject` | no       | Objectives' `ACADEMIC_SUBJECTS` label, e.g. `Science`.           |
 * | `grade`   | no       | One of Objectives' `GRADE_LEVELS`, e.g. `2` or `K`.              |
 * | `link`    | no       | The item's own link; `http:` or `https:` only, up to 2,048 chars. |
 * | `group`   | no       | The attached material's Collab group id, a UUID.                 |
 * | `material`| no       | The attached material's id, a UUID.                              |
 *
 * `plannerFragment` below builds exactly this, and the tests round-trip it.
 *
 * `group` and `material` come together or not at all (planner collab materials KTD6, R8). With
 * both, the hand-off has a `target` and Collab opens that material's page on the Standards
 * check, once per hand-off, instead of home; the expected standards apply there as anywhere.
 * Either one missing or not a UUID drops the target alone and keeps the rest, so the teacher
 * still lands on home with the banner, as before. An older Collab ignores both keys.
 *
 * Validation is per part. An id list that is not all UUIDs, is empty, holds more than twenty,
 * or does not pair up with the codes makes the whole hand-off void: the expected standards are
 * the point of it, and a list that is partly wrong cannot say which part. A state, subject or
 * grade outside the option lists is dropped on its own, and so is a link that is not
 * http(s). Unknown keys are ignored, so a later Planner can add one without breaking this.
 *
 * ## Why a fragment, and why here
 *
 * A query string reaches Worker logs, referrers and PostHog's `$current_url`; a fragment never
 * leaves the browser. `captureInviteToken()` — still the first call in `main.tsx` — hands a
 * `/from-planner` fragment to `capturePlannerHandoff` and points the address bar at `/`
 * before PostHog or Clerk start. The hand-off is held in `sessionStorage`, like an invitation,
 * because a signed-out teacher goes to Clerk's hosted sign-in and comes back to `/` through a
 * redirect that keeps no fragment. It lives for the tab, which is also how long the banner
 * stays (it survives creating a group and uploading the lesson) unless the teacher dismisses it.
 */

export const PLANNER_HANDOFF_KEY = 'collab.planner-handoff';

export interface PlannerHandoff {
  /** The standards the teacher tagged the Planner item with, in tag order. */
  expected: ExpectedStandard[];
  /** The plan's state, subject and grade, as far as they are in the option lists. */
  context: StandardsContext | null;
  /** The item's link, shown for reference. Collab checks an uploaded file, not a link. */
  link: string | null;
  /** The attached material to open, when the Planner named one (KTD6). Absent otherwise. */
  target?: HandoffTarget;
}

/** A material in a group, both ids UUIDs. Only ids: the material's page asks Collab for the rest. */
export interface HandoffTarget {
  groupId: string;
  materialId: string;
}

const VERSION = '1';
const MAX_IDS = 20;
const MAX_FRAGMENT = 8192;
const MAX_LINK = 2048;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Letters, digits, punctuation, symbols and spaces: no control characters, no markup worries. */
const CODE = /^[\p{L}\p{N}\p{P}\p{S} ]{1,64}$/u;

/** Pure: the decoded elements of a comma-joined list, or null when one will not decode. */
function decodeList(raw: string): string[] | null {
  try {
    return raw.split(',').map((part) => decodeURIComponent(part));
  } catch {
    return null;
  }
}

/** Pure: one decoded value, or undefined when it is missing or will not decode. */
function decodeOne(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return undefined;
  }
}

function safeLink(value: string | undefined): string | null {
  if (!value || value.length > MAX_LINK) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

/** Pure: whether a path is the hand-off's, `/from-planner` with or without a trailing slash. */
export const isPlannerHandoffPath = (pathname: string): boolean => pathname.replace(/\/+$/, '') === ROUTE_FROM_PLANNER;

/** Pure: the hand-off a fragment carries, or null when it is not one. */
export function parsePlannerFragment(hash: string): PlannerHandoff | null {
  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  if (body.length === 0 || body.length > MAX_FRAGMENT) return null;
  const pairs = new Map<string, string>();
  for (const part of body.split('&')) {
    const at = part.indexOf('=');
    if (at <= 0) continue;
    const key = part.slice(0, at);
    if (!pairs.has(key)) pairs.set(key, part.slice(at + 1));
  }
  if (pairs.get('v') !== VERSION || pairs.get('from') !== ARRIVAL_SOURCE) return null;

  const ids = decodeList(pairs.get('ids') ?? '');
  const codes = decodeList(pairs.get('codes') ?? '');
  if (!ids || !codes || ids.length === 0 || ids.length > MAX_IDS || ids.length !== codes.length) return null;
  if (!ids.every((id) => UUID.test(id)) || !codes.every((code) => CODE.test(code))) return null;

  const state = decodeOne(pairs.get('state'));
  const subject = decodeOne(pairs.get('subject'));
  const grade = decodeOne(pairs.get('grade'));
  const link = decodeOne(pairs.get('link'));
  const groupId = decodeOne(pairs.get('group'));
  const materialId = decodeOne(pairs.get('material'));
  const handoff: PlannerHandoff = {
    // An id tagged twice is expected once.
    expected: dedupeExpected(ids.map((id, i) => ({ id, code: codes[i]! }))),
    context: pickContext({ jurisdiction: state, academicSubject: subject, grade }),
    link: safeLink(link),
  };
  if (groupId && materialId && UUID.test(groupId) && UUID.test(materialId)) {
    handoff.target = { groupId, materialId };
  }
  return handoff;
}

/** Pure: the fragment for a hand-off, `#` included. The Planner's link builder mirrors this. */
export function plannerFragment(handoff: PlannerHandoff): string {
  const list = (values: readonly string[]) => values.map(encodeURIComponent).join(',');
  const parts = [
    `v=${VERSION}`, `from=${ARRIVAL_SOURCE}`,
    `ids=${list(handoff.expected.map((s) => s.id))}`,
    `codes=${list(handoff.expected.map((s) => s.code))}`,
  ];
  const { jurisdiction, academicSubject, grade } = handoff.context ?? {};
  if (jurisdiction) parts.push(`state=${encodeURIComponent(jurisdiction)}`);
  if (academicSubject) parts.push(`subject=${encodeURIComponent(academicSubject)}`);
  if (grade) parts.push(`grade=${encodeURIComponent(grade)}`);
  if (handoff.link) parts.push(`link=${encodeURIComponent(handoff.link)}`);
  if (handoff.target) {
    parts.push(`group=${encodeURIComponent(handoff.target.groupId)}`);
    parts.push(`material=${encodeURIComponent(handoff.target.materialId)}`);
  }
  return `#${parts.join('&')}`;
}

/** The storage this module needs, passed in. Null whenever storage is unavailable. */
export type HandoffStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;

/**
 * What happens once each per hand-off: the arrival and the first check are reported (KTD7),
 * and a hand-off with a target opens its material (planner collab materials KTD6).
 */
export type HandoffReport = 'arrival' | 'check' | 'open';

interface Held { fragment: string; reported: HandoffReport[] }

/**
 * The held hand-off for this page load, beside `sessionStorage`. When storage is unavailable
 * or refuses, this is what lets `App` still apply the hand-off this page load arrived with; it
 * is gone on reload, as a fragment would be.
 */
let inMemory: Held | null = null;

function readStored(storage: HandoffStorage): string | null {
  try {
    return storage?.getItem(PLANNER_HANDOFF_KEY) ?? null;
  } catch {
    return null;
  }
}

function readHeld(storage: HandoffStorage): Held | null {
  const stored = readStored(storage);
  if (stored === null) return inMemory;
  try {
    const raw: unknown = JSON.parse(stored);
    if (typeof raw !== 'object' || raw === null) return null;
    const { fragment, reported } = raw as Record<string, unknown>;
    if (typeof fragment !== 'string') return null;
    const kinds = Array.isArray(reported)
      ? reported.filter((k): k is HandoffReport => k === 'arrival' || k === 'check' || k === 'open')
      : [];
    return { fragment, reported: kinds };
  } catch {
    return null;
  }
}

function writeHeld(storage: HandoffStorage, held: Held): void {
  inMemory = held;
  try {
    storage?.setItem(PLANNER_HANDOFF_KEY, JSON.stringify(held));
  } catch {
    // Storage refused. The in-memory copy still serves this page load; a sign-in round trip loses it.
  }
}

/**
 * Hold a `/from-planner` fragment. Answers whether it was a hand-off. A new one replaces any
 * held before it and starts its reports over; one that does not parse changes nothing.
 * Called only by `captureInviteToken`, after the address bar no longer shows it.
 */
export function capturePlannerHandoff(hash: string, storage: HandoffStorage): boolean {
  if (parsePlannerFragment(hash) === null) return false;
  writeHeld(storage, { fragment: hash.startsWith('#') ? hash.slice(1) : hash, reported: [] });
  return true;
}

/** A held hand-off, and the two things done with it. */
export interface PlannerSession {
  readonly handoff: PlannerHandoff;
  /** True the first time it is asked for each kind after this hand-off arrived, then false. */
  takeReport(kind: HandoffReport): boolean;
  /** The teacher closed the banner: the hand-off is gone for this tab. */
  dismiss(): void;
}

/** The hand-off held for this tab, re-validated on every read, or null. */
export function plannerSession(storage: HandoffStorage): PlannerSession | null {
  const held = readHeld(storage);
  const handoff = held ? parsePlannerFragment(held.fragment) : null;
  if (!held || !handoff) return null;
  return {
    handoff,
    takeReport(kind) {
      const current = readHeld(storage);
      if (!current || current.fragment !== held.fragment || current.reported.includes(kind)) return false;
      writeHeld(storage, { ...current, reported: [...current.reported, kind] });
      return true;
    },
    dismiss() {
      inMemory = null;
      try {
        storage?.removeItem(PLANNER_HANDOFF_KEY);
      } catch {
        // Nothing to do, and nothing worth telling a teacher about.
      }
    },
  };
}

/** The real browser's session storage, or null. */
export function browserHandoffStorage(): HandoffStorage {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
